/**
 * Normalizers for chat message media.
 *
 * The ms-agents API has three overlapping attachment shapes and the frontend
 * must render all of them:
 *
 *  1. `message.attachments[]` — the current contract. Each row may be *nested*
 *     (`{ media: {...}, caption, attachment_purpose, food_items }`, the shape
 *     the send endpoint documents) or *flattened* by the response serializer.
 *  2. `message.attachment` — the deprecated singular mirror of `attachments[0]`.
 *  3. The Composer's local optimistic shape (`{ data_url, content_type, name }`),
 *     which renders before the server echo lands.
 *
 * Everything here is pure so it can be unit-tested without mocking axios.
 */

/** Canonical attachment kinds. */
export const ATTACHMENT_KINDS = {
  IMAGE: "IMAGE",
  VIDEO: "VIDEO",
  PDF: "PDF",
  VOICE: "VOICE",
  FILE: "FILE",
};

/** Singular/plural human labels per kind. */
const KIND_LABELS = {
  IMAGE: ["photo", "photos"],
  VIDEO: ["video", "videos"],
  PDF: ["PDF", "PDFs"],
  VOICE: ["voice note", "voice notes"],
  FILE: ["file", "files"],
};

/** Free-form kind tokens (media_kind, content_type_bucket) → canonical kind. */
const KIND_BY_TOKEN = {
  image: ATTACHMENT_KINDS.IMAGE,
  photo: ATTACHMENT_KINDS.IMAGE,
  video: ATTACHMENT_KINDS.VIDEO,
  pdf: ATTACHMENT_KINDS.PDF,
  document: ATTACHMENT_KINDS.PDF,
  voice: ATTACHMENT_KINDS.VOICE,
  audio: ATTACHMENT_KINDS.VOICE,
  file: ATTACHMENT_KINDS.FILE,
};

/** File extension → canonical kind, the last resort when no MIME is present. */
const KIND_BY_EXTENSION = {
  jpg: ATTACHMENT_KINDS.IMAGE,
  jpeg: ATTACHMENT_KINDS.IMAGE,
  png: ATTACHMENT_KINDS.IMAGE,
  gif: ATTACHMENT_KINDS.IMAGE,
  webp: ATTACHMENT_KINDS.IMAGE,
  heic: ATTACHMENT_KINDS.IMAGE,
  heif: ATTACHMENT_KINDS.IMAGE,
  avif: ATTACHMENT_KINDS.IMAGE,
  bmp: ATTACHMENT_KINDS.IMAGE,
  mp4: ATTACHMENT_KINDS.VIDEO,
  mov: ATTACHMENT_KINDS.VIDEO,
  webm: ATTACHMENT_KINDS.VIDEO,
  m4v: ATTACHMENT_KINDS.VIDEO,
  avi: ATTACHMENT_KINDS.VIDEO,
  mkv: ATTACHMENT_KINDS.VIDEO,
  mp3: ATTACHMENT_KINDS.VOICE,
  m4a: ATTACHMENT_KINDS.VOICE,
  aac: ATTACHMENT_KINDS.VOICE,
  ogg: ATTACHMENT_KINDS.VOICE,
  oga: ATTACHMENT_KINDS.VOICE,
  opus: ATTACHMENT_KINDS.VOICE,
  wav: ATTACHMENT_KINDS.VOICE,
  pdf: ATTACHMENT_KINDS.PDF,
};

/** attachment_purpose → badge label. GENERAL is deliberately unlabelled. */
const PURPOSE_LABELS = {
  FOOD_PHOTO: "Food photo",
  BODY_PHOTO: "Body photo",
  METRIC_PROOF: "Metric proof",
  QUESTION_VIDEO: "Question",
};

/** metric_code → human label. */
const METRIC_LABELS = {
  WEIGHT: "Weight",
  HEIGHT: "Height",
  WAIST: "Waist",
  HIP: "Hip",
  CHEST: "Chest",
  ARM: "Arm",
  THIGH: "Thigh",
  BODY_FAT_PCT: "Body fat",
  OTHER: "Metric",
};

/**
 * @typedef {Object} ChatFoodItem
 * @property {string} itemName
 * @property {number|null} quantity
 * @property {string} unitLabel
 * @property {string|null} source - "USER" when the client reported it themselves.
 */

/**
 * @typedef {Object} ChatAttachment
 * @property {string} id - Stable React key.
 * @property {number} index - Position in the message's attachment list.
 * @property {string} blobPath - Storage identity; what /uploads/media-url is
 *   keyed on, and the media cache key. Empty on legacy rows.
 * @property {string} url - Raw data_url. NOT directly fetchable (the container
 *   is private) — resolve it with useMediaUrl, never bind it to a src.
 * @property {'IMAGE'|'VIDEO'|'PDF'|'VOICE'|'FILE'} kind
 * @property {string} mimeType
 * @property {string} fileName
 * @property {number|null} fileSize
 * @property {number|null} width
 * @property {number|null} height
 * @property {number|null} durationMs
 * @property {number|null} pageCount
 * @property {string} caption
 * @property {string|null} purpose - Raw attachment_purpose, null when GENERAL/absent.
 * @property {string|null} purposeLabel
 * @property {ChatFoodItem[]} foodItems
 */

/**
 * @typedef {Object} ChatMetric
 * @property {string} code
 * @property {string} label
 * @property {number|null} value
 * @property {string} unit
 * @property {string|null} measuredAt
 * @property {number|null} attachmentIndex - Index of the photo proof, if any.
 */

/**
 * Read a field from an attachment row, preferring the nested `media` object.
 * @param {Object} row
 * @param {string} key
 * @returns {*}
 */
const pick = (row, key) => {
  const nested = row?.media?.[key];
  if (nested !== undefined && nested !== null) return nested;
  return row?.[key];
};

/**
 * First non-empty string among the given keys.
 * @param {Object} row
 * @param {string[]} keys
 * @returns {string}
 */
const pickString = (row, keys) => {
  for (const key of keys) {
    const value = pick(row, key);
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
};

/**
 * Coerce to a finite number, or null.
 * @param {*} value
 * @returns {number|null}
 */
const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Lowercase file extension of a path or URL, query string stripped.
 * @param {string} nameOrUrl
 * @returns {string}
 */
const extensionOf = (nameOrUrl) => {
  const path = String(nameOrUrl).split(/[?#]/)[0];
  const base = path.split("/").pop() || "";
  const dot = base.lastIndexOf(".");
  return dot > 0 ? base.slice(dot + 1).toLowerCase() : "";
};

/**
 * Best-effort file name from a blob URL, with the SAS query removed.
 * @param {string} url
 * @returns {string}
 */
const fileNameFromUrl = (url) => {
  if (!url) return "";
  const base = String(url).split(/[?#]/)[0].split("/").pop() || "";
  try {
    return decodeURIComponent(base);
  } catch (err) {
    return base;
  }
};

/**
 * Resolve an attachment row to a canonical kind. First hit wins:
 * explicit kind token → MIME type → file extension → FILE.
 *
 * @param {Object} row
 * @returns {'IMAGE'|'VIDEO'|'PDF'|'VOICE'|'FILE'}
 */
const resolveKind = (row) => {
  const contentType = pickString(row, ["content_type"]);
  const tokens = [
    pickString(row, ["media_kind"]),
    pickString(row, ["content_type_bucket"]),
    pickString(row, ["kind"]),
    // The optimistic local shape stores a bucket ("IMAGE") under content_type.
    contentType.includes("/") ? "" : contentType,
  ];
  for (const token of tokens) {
    const hit = KIND_BY_TOKEN[token.toLowerCase()];
    if (hit) return hit;
  }

  const mime = (pickString(row, ["mime_type"]) || contentType).toLowerCase();
  if (mime.startsWith("image/")) return ATTACHMENT_KINDS.IMAGE;
  if (mime.startsWith("video/")) return ATTACHMENT_KINDS.VIDEO;
  if (mime.startsWith("audio/")) return ATTACHMENT_KINDS.VOICE;
  if (mime === "application/pdf") return ATTACHMENT_KINDS.PDF;

  const name =
    pickString(row, ["original_file_name", "file_name", "name"]) ||
    pickString(row, ["data_url", "url"]);
  return KIND_BY_EXTENSION[extensionOf(name)] || ATTACHMENT_KINDS.FILE;
};

/**
 * Normalize one `food_items` entry.
 * @param {Object} item
 * @returns {ChatFoodItem}
 */
const normalizeFoodItem = (item) => ({
  itemName:
    (typeof item?.item_name === "string" && item.item_name.trim()) ||
    (typeof item?.name === "string" && item.name.trim()) ||
    "Food item",
  quantity: toNumber(item?.quantity),
  unitLabel:
    (typeof item?.quantity_unit_label === "string" &&
      item.quantity_unit_label.trim()) ||
    (typeof item?.quantity_type === "string" && item.quantity_type.trim()) ||
    "",
  source: item?.source || null,
});

/**
 * Normalize a single attachment row into a render-ready shape.
 * @param {Object} row
 * @param {number} index
 * @returns {ChatAttachment|null}
 */
const normalizeRow = (row, index) => {
  if (!row || typeof row !== "object") return null;

  const url = pickString(row, ["data_url", "url"]);
  const kind = resolveKind(row);
  const purposeRaw = pickString(row, ["attachment_purpose", "purpose"]);
  const purpose = purposeRaw && purposeRaw !== "GENERAL" ? purposeRaw : null;
  const fileName =
    pickString(row, ["original_file_name", "file_name", "name"]) ||
    fileNameFromUrl(url);
  const rawFoodItems = row.food_items || row.media?.food_items;

  const blobPath = pickString(row, ["blob_path"]);

  return {
    id: String(row.id || blobPath || url || `attachment-${index}`),
    index,
    blobPath,
    url,
    kind,
    mimeType: pickString(row, ["mime_type"]) || "",
    fileName,
    fileSize: toNumber(pick(row, "file_size")),
    width: toNumber(pick(row, "width")),
    height: toNumber(pick(row, "height")),
    durationMs: toNumber(pick(row, "duration_ms")),
    pageCount: toNumber(pick(row, "page_count")),
    caption: pickString(row, ["caption"]),
    purpose,
    purposeLabel: purpose ? PURPOSE_LABELS[purpose] || null : null,
    foodItems: Array.isArray(rawFoodItems)
      ? rawFoodItems.map(normalizeFoodItem)
      : [],
  };
};

/**
 * Every attachment on a message, ordered and render-ready.
 *
 * Prefers `attachments[]`, falls back to the deprecated singular `attachment`.
 * Rows are ordered by the server-assigned `position` when present (the API
 * guarantees it is stable) and otherwise keep array order.
 *
 * @param {Object|null} message
 * @returns {ChatAttachment[]}
 */
export function normalizeAttachments(message) {
  if (!message) return [];

  let rows = [];
  if (Array.isArray(message.attachments) && message.attachments.length) {
    rows = message.attachments;
  } else if (message.attachment) {
    rows = [message.attachment];
  }

  return rows
    .map((row, i) => ({ row, order: toNumber(pick(row, "position")) ?? i }))
    .sort((a, b) => a.order - b.order)
    .map(({ row }, i) => normalizeRow(row, i))
    .filter(Boolean);
}

/**
 * Message-level body/weight readings, render-ready.
 * @param {Object|null} message
 * @returns {ChatMetric[]}
 */
export function normalizeMetrics(message) {
  const rows = message?.metrics;
  if (!Array.isArray(rows)) return [];

  return rows
    .filter((m) => m && typeof m === "object")
    .map((m) => {
      const code = String(m.metric_code || m.code || "OTHER").toUpperCase();
      return {
        code,
        label: METRIC_LABELS[code] || METRIC_LABELS.OTHER,
        value: toNumber(m.value),
        unit: typeof m.unit === "string" ? m.unit : "",
        measuredAt: m.measured_at || null,
        attachmentIndex: toNumber(m.attachment_index),
      };
    });
}

/**
 * Human label for an attachment kind, e.g. "photo" / "voice notes".
 * @param {string} kind
 * @param {number} [count=1] - Pluralizes when not 1.
 * @returns {string}
 */
export function attachmentKindLabel(kind, count = 1) {
  const pair = KIND_LABELS[kind] || KIND_LABELS.FILE;
  return count === 1 ? pair[0] : pair[1];
}

/**
 * One-line description of a message's attachments, for previews and toasts.
 * Returns "" when there are none.
 *
 * @param {ChatAttachment[]} attachments
 * @returns {string}
 */
export function describeAttachments(attachments) {
  if (!attachments || !attachments.length) return "";
  const kinds = new Set(attachments.map((a) => a.kind));
  if (kinds.size === 1) {
    const kind = attachments[0].kind;
    const label = attachmentKindLabel(kind, attachments.length);
    return attachments.length === 1 ? label : `${attachments.length} ${label}`;
  }
  return `${attachments.length} attachments`;
}

/**
 * Compact byte-size label, e.g. "1.4 MB". Returns "" for unknown sizes.
 * @param {number|null} bytes
 * @returns {string}
 */
export function formatFileSize(bytes) {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}

/**
 * Duration as m:ss. Returns "" for unknown durations.
 * @param {number|null} ms
 * @returns {string}
 */
export function formatDuration(ms) {
  if (!ms || ms <= 0) return "";
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}
